import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { X, Plus } from "lucide-react";

export default function Settings() {
  const [d, setD] = useState(null);
  const [step, setStep] = useState("");

  const refresh = async () => {
    const r = await api.get("/dealership");
    setD(r.data);
  };
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    try {
      await api.patch("/dealership/settings", {
        name: d.name,
        lead_routing_email: d.lead_routing_email,
        power_rotation_minutes: parseInt(d.power_rotation_minutes) || 30,
        position_float_cap: parseInt(d.position_float_cap) || 3,
        process_steps: d.process_steps,
      });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const removeStep = (i) => setD({ ...d, process_steps: d.process_steps.filter((_, idx) => idx !== i) });
  const addStep = () => {
    if (!step.trim()) return;
    setD({ ...d, process_steps: [...d.process_steps, step.trim()] });
    setStep("");
  };

  if (!d) return <Layout><div className="p-8 text-white/30">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-3xl mx-auto">
        <div className="border-b border-white/10 pb-3 mb-6">
          <h1 className="font-display text-4xl font-black uppercase tracking-tight">Settings</h1>
          <p className="text-xs uppercase tracking-[0.3em] text-white/40 mt-1">Store Configuration</p>
        </div>

        <div className="space-y-6">
          <section className="bg-[#141414] border border-white/10 p-5 rounded-sm">
            <h2 className="font-display text-lg font-black uppercase tracking-wider mb-4">Dealership</h2>
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Store Name</Label>
                <Input className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm" value={d.name || ""} onChange={e => setD({ ...d, name: e.target.value })} data-testid="store-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">CRM Lead Routing Email</Label>
                <Input className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm" placeholder="leads@your-crm.com" value={d.lead_routing_email || ""} onChange={e => setD({ ...d, lead_routing_email: e.target.value })} data-testid="lead-email-input" />
                <p className="text-[10px] uppercase tracking-widest text-white/30 mt-2">
                  Closed opportunities are emailed here as structured leads (Mailgun).
                </p>
              </div>
            </div>
          </section>

          <section className="bg-[#141414] border border-white/10 p-5 rounded-sm">
            <h2 className="font-display text-lg font-black uppercase tracking-wider mb-4">Queue Behavior</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Power Rotation (minutes)</Label>
                <Input type="number" className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm tabular" value={d.power_rotation_minutes} onChange={e => setD({ ...d, power_rotation_minutes: e.target.value })} data-testid="rotation-minutes-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60">Position Float Cap</Label>
                <Input type="number" className="bg-[#0A0A0A] border-white/10 mt-2 rounded-sm tabular" value={d.position_float_cap} onChange={e => setD({ ...d, position_float_cap: e.target.value })} data-testid="float-cap-input" />
              </div>
            </div>
          </section>

          <section className="bg-[#141414] border border-white/10 p-5 rounded-sm">
            <h2 className="font-display text-lg font-black uppercase tracking-wider mb-4">Sales Process Steps</h2>
            <div className="space-y-2">
              {(d.process_steps || []).map((s, i) => (
                <div key={i} className="flex items-center gap-2 bg-[#0A0A0A] border border-white/5 px-3 py-2">
                  <span className="font-display tabular font-bold text-white/30 w-6">{i + 1}</span>
                  <span className="flex-1 font-bold uppercase tracking-wide text-sm">{s}</span>
                  <Button size="sm" variant="ghost" onClick={() => removeStep(i)} className="text-white/40 hover:text-[#FF3B30]" data-testid={`remove-step-${i}`}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Input placeholder="Add step name…" value={step} onChange={e => setStep(e.target.value)} className="bg-[#0A0A0A] border-white/10 rounded-sm" data-testid="new-step-input" />
              <Button onClick={addStep} className="bg-white text-black hover:bg-white/90 rounded-sm uppercase tracking-wider font-bold" data-testid="add-step-btn">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </section>

          <div className="flex justify-end">
            <Button onClick={save} className="bg-[#34C759] text-black hover:bg-[#2bb850] rounded-sm uppercase tracking-wider font-bold h-12 px-8" data-testid="save-settings-btn">
              Save Configuration
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
